import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Fade,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useAppContext } from 'contexts/AppContext';
import defaultLocales from 'globals/locales/default';
import { isSuccessState } from 'globals/utils';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsPagination from 'ui-elements/tabs/FsPagination';
import FsInvitationsTable from 'ui-elements/tables/FsInvitationsTable';
import FsDefaultDialog from 'ui-elements/modals/FsDefaultDialog';
import FsButton from 'ui-elements/buttons/FsButton';
import FsMagicLinkForm from 'ui-elements/forms/FsMagicLinkForm';
import FsSubtitle from 'ui-elements/typography/FsSubtitle';
import FsInviteNewCustomerForm from 'ui-elements/forms/FsInviteNewCustomerForm';
import FsMinWidthScrollBox from 'ui-elements/boxes/FsMinWidthScrollBox';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import { PaginationWrapperBox } from './Invitations.style-elements';
import { dummyPagination, headLabelsLongVersion } from './Invitations.utils';

enum InvitationsModalType {
  EMAIL,
  MAGIC_LINK,
}

const Invitations: React.FC = () => {
  const {
    invitationsList: { status, getInvitations, init: initStore },
  } = useAppContext();
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [showMagicLinkModal, setShowMagicLinkModal] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();
  const contextualMenuOpen = Boolean(anchorEl);
  const url = 'https://friendlyscore.com/share/openbanking-demo';
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const buttons = [
    <FsButton
      variant="contained"
      color="primary"
      key={1}
      onClick={(e) => setAnchorEl(e.currentTarget)}
    >
      {isUpSm ? defaultLocales.inviteNewCustomer : defaultLocales.invite}
    </FsButton>,
  ];

  const handleCloseContextualMenu = (): void => {
    setAnchorEl(undefined);
  };

  const handleMenuItemClick = (modalType: InvitationsModalType) => {
    handleCloseContextualMenu();
    if (modalType === InvitationsModalType.EMAIL) setShowEmailModal(true);
    if (modalType === InvitationsModalType.MAGIC_LINK)
      setShowMagicLinkModal(true);
  };

  useEffect(() => {
    initStore();
  }, [initStore]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
      />
    );

  return (
    <Fade in timeout={1000}>
      <FsPaper
        data-test="invitations-page"
        title={defaultLocales.invitationsPage.title}
        buttons={buttons}
      >
        <FsMinWidthScrollBox breakpointDown="md" minWidth={600}>
          <FsInvitationsTable
            rows={getInvitations()}
            headLabels={headLabelsLongVersion}
            isLongVersion
          />
        </FsMinWidthScrollBox>
        <FsMinWidthScrollBox minWidth={290}>
          <PaginationWrapperBox>
            <FsPagination paginationInfo={dummyPagination} />
          </PaginationWrapperBox>
        </FsMinWidthScrollBox>

        <Menu
          anchorEl={anchorEl}
          open={contextualMenuOpen}
          onClose={handleCloseContextualMenu}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem
            onClick={() => handleMenuItemClick(InvitationsModalType.EMAIL)}
          >
            <span>{defaultLocales.email}</span>
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick(InvitationsModalType.MAGIC_LINK)}
          >
            <span>{defaultLocales.magicLink}</span>
          </MenuItem>
        </Menu>

        <FsDefaultDialog
          disableBackdropClick
          disableEscapeKeyDown
          title={defaultLocales.inviteCustomer}
          isOpen={showEmailModal}
          fullWidth
          maxWidth="sm"
          onClose={() => setShowEmailModal(false)}
        >
          <FsInviteNewCustomerForm onClose={() => setShowEmailModal(false)} />
        </FsDefaultDialog>

        <FsDefaultDialog
          title={defaultLocales.inviteCustomer}
          isOpen={showMagicLinkModal}
          fullWidth
          maxWidth="sm"
          onClose={() => setShowMagicLinkModal(false)}
        >
          <Box>
            <Box mb={2}>
              <FsSubtitle>
                {defaultLocales.inviteCustomerWithMagicLink}
              </FsSubtitle>
            </Box>
            <FsMagicLinkForm url={url} />
          </Box>
        </FsDefaultDialog>
      </FsPaper>
    </Fade>
  );
};

export default observer(Invitations);
